import { DateTime } from 'luxon';
import { FormControl } from '@angular/forms';

/**
 * Splits Date i to Date and Time strings
 * @param date
 */
export const splitDateAndTime = (date: string | Date): { date: string; time: string } => {
  const d =
    date instanceof Date ? DateTime.fromJSDate(date as Date) : DateTime.fromISO(date as string);
  const dt = date ? d : DateTime.now();
  return {
    date: dt.toISODate()!,
    time: dt.toFormat('HH:mm'),
  };
};

/**
 * Concatenates date and time strings to Date object.
 * @param date
 * @param time
 */
export const concatDateAndTime = (date: string, time: string): Date => {
  const { date: splitDate } = splitDateAndTime(date);
  const [hours, minutes] = time.split(':').map(Number);
  return DateTime.fromISO(splitDate).set({ hour: hours, minute: minutes }).toJSDate();
};

export const syncDateAndTimeControls = (
  dateCtrl: FormControl,
  timeCtrl: FormControl,
  targetCtrl: FormControl
): (() => void) => {
  const subscriptions = [
    dateCtrl.valueChanges.subscribe(() => sync()),
    timeCtrl.valueChanges.subscribe(() => sync()),
  ];
  const sync = () => {
    try {
      const dateValue = dateCtrl?.getRawValue();
      const timeValue = timeCtrl?.getRawValue();
      if (!dateValue || !timeValue) {
        return;
      }
      const time = new Date(timeValue);
      if (isNaN(time.getTime())) {
        return;
      }
      const hours = time.getHours();
      const minutes = time.getMinutes();
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return;
      }
      const result = new Date(date);
      result.setHours(hours);
      result.setMinutes(minutes);
      targetCtrl.setValue(result, { emitEvent: false });
    } catch (error) {
      console.error('Error syncing date and time controls:', error);
    }
  };
  // Return cleanup function
  return () => subscriptions.forEach(sub => sub.unsubscribe());
};

export const formatToClickHouseDate = (date: Date) => {
  const pad = (num: number, size: number = 2) => num.toString().padStart(size, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // getMonth() is zero-based
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3); // Ensure 3-digit milliseconds

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const formatHour = (hour: number) => {
  return DateTime.fromObject({ hour }).toFormat('h a');
};

export const formatDate = (date: Date, format: string = 'MMM d'): string => {
  return DateTime.fromJSDate(date).toFormat(format);
};
